// ─── src/pages/AboutPage.jsx (Admin Panel) ───────────────────────────────────
// Manages the single 'about/main' document in Firestore.
// Fields: heading, philosophyWords[], paragraphs[], stats[], tags[],
//         techStack[], profileImg (URL), resumeUrl

import React, { useEffect, useState } from 'react';
import {
  Card, Button, Input, Form, message, Spin,
  Typography, Divider, Space,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { REST_BASE, API_KEY } from '../firebase/config';

const { Title, Text } = Typography;
const { TextArea } = Input;

// ── REST helpers ──────────────────────────────────────────────
function toValue(v) {
  if (v === null || v === undefined) return { nullValue: null };
  if (typeof v === 'boolean')        return { booleanValue: v };
  if (typeof v === 'number')         return Number.isInteger(v) ? { integerValue: String(v) } : { doubleValue: v };
  if (typeof v === 'string')         return { stringValue: v };
  if (Array.isArray(v))              return { arrayValue: { values: v.map(toValue) } };
  if (typeof v === 'object')         return { mapValue: { fields: toFields(v) } };
  return { stringValue: String(v) };
}
function toFields(obj) {
  const f = {};
  for (const [k, v] of Object.entries(obj)) f[k] = toValue(v);
  return f;
}
function fromValue(v) {
  if (!v) return null;
  if ('stringValue'  in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue'  in v) return Number(v.doubleValue);
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue'    in v) return null;
  if ('arrayValue'   in v) return (v.arrayValue.values || []).map(fromValue);
  if ('mapValue'     in v) { const o={}; for(const[k,val]of Object.entries(v.mapValue.fields||{})) o[k]=fromValue(val); return o; }
  return null;
}
function fromFields(fields) {
  const o = {};
  for (const [k, v] of Object.entries(fields)) o[k] = fromValue(v);
  return o;
}

async function loadAbout() {
  const res = await fetch(`${REST_BASE}/about/main?key=${API_KEY}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Load failed: ${res.status}`);
  const json = await res.json();
  return fromFields(json.fields || {});
}

async function saveAbout(data) {
  const res = await fetch(`${REST_BASE}/about/main?key=${API_KEY}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields: toFields(data) }),
  });
  if (!res.ok) { const e = await res.json().catch(()=>{}); throw new Error(e?.error?.message || `Save failed: ${res.status}`); }
}

const DEFAULT = {
  heading: 'Building the\nDigital Future',
  philosophyWords: ['I','build','digital','experiences','where','design','meets','logic','and','code','creates','impact.'],
  paragraphs: [
    "I'm a Full Stack Developer with 3+ years of experience building responsive and scalable web applications using modern technologies.",
    "My passion lies in creating clean UI experiences and developing efficient backend systems that deliver smooth user experiences.",
    "I have worked on multiple real-world projects, focusing on frontend development, API integration, and building user-friendly applications.",
  ],
  stats: [{ val:'3+', label:'Yrs Exp' }, { val:'5+', label:'Projects' }, { val:'99%', label:'Satisfaction' }],
  tags: ['React','javascript','Node.js','Python','Html','PostgreSQL','Css','express','mysql'],
  techStack: ['React','Express.js','MySql'],
  profileImg: './profile2.png',
  resumeUrl: './naveen_updated.pdf',
};

// ── Responsive row: items stack on narrow screens ─────────────
const itemRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 8,
  alignItems: 'flex-start',
};

// Full-width inputs (no fixed px widths)
const inputStyle    = { flex: '1 1 120px', minWidth: 0 };
const textareaStyle = { flex: '1 1 200px', minWidth: 0 };
const valInputStyle = { flex: '0 1 90px',  minWidth: 60 };
const lblInputStyle = { flex: '1 1 120px', minWidth: 80 };

export default function AboutPage() {
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [form]     = Form.useForm();

  // array states
  const [philosophyWords, setPhilosophyWords] = useState(DEFAULT.philosophyWords);
  const [paragraphs,      setParagraphs]      = useState(DEFAULT.paragraphs);
  const [stats,           setStats]           = useState(DEFAULT.stats);
  const [tags,            setTags]            = useState(DEFAULT.tags);
  const [techStack,       setTechStack]       = useState(DEFAULT.techStack);

  useEffect(() => {
    loadAbout().then(data => {
      const d = data || DEFAULT;
      form.setFieldsValue({ heading: d.heading, profileImg: d.profileImg, resumeUrl: d.resumeUrl });
      setPhilosophyWords(d.philosophyWords || DEFAULT.philosophyWords);
      setParagraphs(d.paragraphs || DEFAULT.paragraphs);
      setStats(d.stats || DEFAULT.stats);
      setTags(d.tags || DEFAULT.tags);
      setTechStack(d.techStack || DEFAULT.techStack);
    }).catch(e => message.error('Load failed: ' + e.message))
      .finally(() => setLoading(false));
  }, [form]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      await saveAbout({
        heading: vals.heading,
        profileImg: vals.profileImg,
        resumeUrl: vals.resumeUrl,
        philosophyWords,
        paragraphs,
        stats,
        tags,
        techStack,
      });
      message.success('About section saved!');
    } catch (e) {
      message.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin style={{ display:'block', marginTop:80 }} />;

  return (
    // Outer wrapper: full width, horizontal scroll prevention
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      {/* ── Header ── */}
      <div style={{
        marginBottom: 24,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
      }}>
        <Title level={4} style={{ margin: 0 }}>About Section</Title>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          // On very small screens the button spans full width for easy tapping
          style={{ minWidth: 120 }}
        >
          Save All
        </Button>
      </div>

      <Form form={form} layout="vertical">

        {/* ── Basic ── */}
        <Card title="Basic" style={{ marginBottom: 16 }}>
          <Form.Item
            name="heading"
            label='Heading (use \n to split into two lines)'
            rules={[{ required: true }]}
          >
            <Input placeholder="Building the\nDigital Future" />
          </Form.Item>
          <Form.Item name="profileImg" label="Profile Image URL">
            <Input placeholder="./profile2.png or https://..." />
          </Form.Item>
          <Form.Item name="resumeUrl" label="Resume File URL">
            <Input placeholder="./naveen_updated.pdf" />
          </Form.Item>
        </Card>

        {/* ── Philosophy Words ── */}
        <Card title="Philosophy Words (scroll-reveal sentence)" style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Each word animates in on scroll. Add/remove words freely.
          </Text>
          {philosophyWords.map((w, i) => (
            <div key={i} style={itemRowStyle}>
              <Input
                value={w}
                onChange={e => {
                  const a = [...philosophyWords];
                  a[i] = e.target.value;
                  setPhilosophyWords(a);
                }}
                style={inputStyle}
              />
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setPhilosophyWords(philosophyWords.filter((_, idx) => idx !== i))}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setPhilosophyWords([...philosophyWords, ''])}
            style={{ marginTop: 4 }}
          >
            Add Word
          </Button>
        </Card>

        {/* ── Bio Paragraphs ── */}
        <Card title="Bio Paragraphs" style={{ marginBottom: 16 }}>
          {paragraphs.map((p, i) => (
            <div key={i} style={itemRowStyle}>
              <TextArea
                value={p}
                rows={2}
                onChange={e => {
                  const a = [...paragraphs];
                  a[i] = e.target.value;
                  setParagraphs(a);
                }}
                style={textareaStyle}
              />
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setParagraphs(paragraphs.filter((_, idx) => idx !== i))}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setParagraphs([...paragraphs, ''])}
            style={{ marginTop: 4 }}
          >
            Add Paragraph
          </Button>
        </Card>

        {/* ── Stats ── */}
        <Card title="Stats (val + label)" style={{ marginBottom: 16 }}>
          {stats.map((s, i) => (
            <div key={i} style={itemRowStyle}>
              <Input
                placeholder="3+"
                value={s.val}
                onChange={e => {
                  const a = [...stats];
                  a[i] = { ...a[i], val: e.target.value };
                  setStats(a);
                }}
                style={valInputStyle}
              />
              <Input
                placeholder="Yrs Exp"
                value={s.label}
                onChange={e => {
                  const a = [...stats];
                  a[i] = { ...a[i], label: e.target.value };
                  setStats(a);
                }}
                style={lblInputStyle}
              />
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setStats(stats.filter((_, idx) => idx !== i))}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setStats([...stats, { val: '', label: '' }])}
            style={{ marginTop: 4 }}
          >
            Add Stat
          </Button>
        </Card>

        {/* ── Tech Tags ── */}
        <Card title="Tech Tags" style={{ marginBottom: 16 }}>
          {tags.map((t, i) => (
            <div key={i} style={itemRowStyle}>
              <Input
                value={t}
                onChange={e => {
                  const a = [...tags];
                  a[i] = e.target.value;
                  setTags(a);
                }}
                style={inputStyle}
              />
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setTags(tags.filter((_, idx) => idx !== i))}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setTags([...tags, ''])}
            style={{ marginTop: 4 }}
          >
            Add Tag
          </Button>
        </Card>

        {/* ── Tech Stack ── */}
        <Card title="Tech Stack Chips (shown beside photo on desktop)">
          {techStack.map((t, i) => (
            <div key={i} style={itemRowStyle}>
              <Input
                value={t}
                onChange={e => {
                  const a = [...techStack];
                  a[i] = e.target.value;
                  setTechStack(a);
                }}
                style={inputStyle}
              />
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setTechStack(techStack.filter((_, idx) => idx !== i))}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setTechStack([...techStack, ''])}
            style={{ marginTop: 4 }}
          >
            Add
          </Button>
        </Card>

      </Form>
    </div>
  );
}